import { Component, Input, OnInit } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { GlassCountries } from 'src/app/utils/Constants';

export interface GlassFilterState {
  ug: number | null;        // null = "All"
  structure: string | null;
  lightTransmittance: number | null;
  gValue: number | null;
  category: string | null;
  regionId: number;         // 0 = Worldwide / All
}

export interface GlassFilterOptions {
  ugValues: number[];
  structures: string[];
  lightTransValues: number[];
  gValues: number[];
  categories: string[];
  regions: { id: number; name: string; eu: boolean }[];
}

@Component({
  selector: 'app-glass-filter-modal',
  templateUrl: './glass-filter-modal.component.html',
  styleUrls: ['./glass-filter-modal.component.scss'],
  standalone: false,
})
export class GlassFilterModalComponent implements OnInit {
  /** All available glasses (already pre-filtered by manufacturer if by-brand) */
  @Input() glasses: any[] = [];
  /** Countries list for region dropdown */
  @Input() countries: { id: number; name: string }[] = [];
  /** Current active filter state passed in from parent */
  @Input() currentFilter: GlassFilterState = {
    ug: null, structure: null, lightTransmittance: null,
    gValue: null, category: null, regionId: 0,
  };
  readonly allCountries = GlassCountries;
  // local working copy
  filter: GlassFilterState = {
    ug: null, structure: null, lightTransmittance: null,
    gValue: null, category: null, regionId: 0,
  };

  options: GlassFilterOptions = {
    ugValues: [], structures: [], lightTransValues: [],
    gValues: [], categories: [], regions: [],
  };

  constructor(private modalController: ModalController) { }

  ngOnInit() {
    // Deep copy incoming filter
    this.filter = { ...this.currentFilter };
    this.rebuildOptions();
  }

  // ─── Dynamic option building ─────────

  private glassBelongsToRegion(glass: any, regionId: number): boolean {
    if (!glass.region || !Array.isArray(glass.region)) return false;
    if (regionId === 0) return true; // "All" / Worldwide

    const neg = -regionId;
    let isEU = false;
    let result = false;

    for (const r of glass.region) {
      if (r === 1) isEU = true;
      if (r === regionId) result = true;
      if (r === 0) result = true;         // Worldwide
      if (r === neg) result = false;      // explicitly excluded
    }

    // Check EU membership
    if (isEU && regionId !== 0) {
      const country = this.allCountries.find(c => c.id === regionId);
      if ((country as any)?.eu) result = true;
    }
    return result;
  }

  /**
   * Re-derive dropdown options from the CURRENT filter state — same
   */
  rebuildOptions() {
    const ugSet = new Set<number>();
    const structureSet = new Set<string>();
    const ltSet = new Set<number>();
    const gSet = new Set<number>();
    const catSet = new Set<string>();
    const usedRegionIds = new Set<number>();
    // Always show all regions for the selected manufacturer/mode
    for (const glass of this.glasses) {
      if (glass.deleted) continue;
      if (!this.glassBelongsToRegion(glass, this.filter.regionId)) continue;

      // Collect used region IDs from glass.region array
      if (Array.isArray(glass.region)) {
        for (const r of glass.region) {
          if (r > 1) usedRegionIds.add(r); // skip 0 (worldwide) and 1 (EU marker)
        }
      }
      const category = this.getCategoryValue(glass);
      // Category filter gate
      if (this.filter.category && category !== this.filter.category) continue;
      catSet.add(category);

      // Structure filter gate
      if (this.filter.structure && glass.structure !== this.filter.structure) continue;
      structureSet.add(glass.structure);

      // Ug filter gate
      const ug = parseFloat(parseFloat(glass.ug).toFixed(1));
      if (this.filter.ug !== null && ug > this.filter.ug) continue;
      ugSet.add(ug);

      // lightTransmittance filter gate
      if (this.filter.lightTransmittance !== null && glass.lightTransmittance < this.filter.lightTransmittance) continue;
      ltSet.add(glass.lightTransmittance);

      // G-Value filter gate
      if (this.filter.gValue !== null && glass.gValue < this.filter.gValue) continue;
      gSet.add(glass.gValue);
    }

    // Build region list from GlassCountries — only countries used by visible glasses
    // id=0 (Worldwide) and id=1 (EU) handled as special entries in the template
    const filteredRegions = this.allCountries.filter(
      c => c.id > 1 && usedRegionIds.has(c.id)
    );

    this.options = {
      ugValues: Array.from(ugSet).sort((a, b) => a - b),
      structures: Array.from(structureSet).sort(),
      lightTransValues: Array.from(ltSet).sort((a, b) => a - b),
      gValues: Array.from(gSet).sort((a, b) => a - b),
      categories: Array.from(catSet).sort(),
      // Worldwide (0) + EU (1) always shown, then specific countries used by glasses
      regions: [
        this.allCountries.find(c => c.id === 0)!,  // Worldwide
        this.allCountries.find(c => c.id === 1)!,  // EU
        ...filteredRegions.sort((a, b) => a.id - b.id),
      ],
    };

    // Auto-reset filters if their current value is no longer in the options
    this.guardFilter();
  }

  /** If a currently-set filter value no longer exists in options, clear it */
  private guardFilter() {
    if (this.filter.ug !== null && !this.options.ugValues.includes(this.filter.ug)) {
      this.filter.ug = null;
    }
    if (this.filter.structure && !this.options.structures.includes(this.filter.structure)) {
      this.filter.structure = null;
    }
    if (this.filter.lightTransmittance !== null && !this.options.lightTransValues.includes(this.filter.lightTransmittance)) {
      this.filter.lightTransmittance = null;
    }
    if (this.filter.gValue !== null && !this.options.gValues.includes(this.filter.gValue)) {
      this.filter.gValue = null;
    }
    if (this.filter.category && !this.options.categories.includes(this.filter.category)) {
      this.filter.category = null;
    }
  }

  private getCategoryValue(glass: any): string {
    return glass?.category?.trim() ? glass.category.trim() : 'Other';
  }
  // ─── Called whenever any dropdown changes ───────────────────────────────────

  onFilterChange() {
    this.rebuildOptions();
  }

  // ─── Count matching glasses for the current filter (live feedback) ──────────

  get matchCount(): number {
    return this.glasses.filter(glass => {
      if (glass.deleted) return false;
      if (!this.glassBelongsToRegion(glass, this.filter.regionId)) return false;
      if (this.filter.category && this.getCategoryValue(glass) !== this.filter.category) return false;
      if (this.filter.structure && glass.structure !== this.filter.structure) return false;
      const ug = parseFloat(parseFloat(glass.ug).toFixed(1));
      if (this.filter.ug !== null && ug > this.filter.ug) return false;
      if (this.filter.lightTransmittance !== null && glass.lightTransmittance < this.filter.lightTransmittance) return false;
      if (this.filter.gValue !== null && glass.gValue < this.filter.gValue) return false;
      return true;
    }).length;
  }

  get isFilterActive(): boolean {
    return this.filter.ug !== null ||
      !!this.filter.structure ||
      this.filter.lightTransmittance !== null ||
      this.filter.gValue !== null ||
      !!this.filter.category ||
      this.filter.regionId !== 0;
  }

  // ─── Actions ─────────────────────────────────────────────────────────────────

  reset() {
    this.filter = {
      ug: null, structure: null, lightTransmittance: null,
      gValue: null, category: null, regionId: 0,
    };
    this.rebuildOptions();
  }

  validate() {
    this.modalController.dismiss({ filter: this.filter }, 'confirm');
  }

  cancel() {
    this.modalController.dismiss(null, 'cancel');
  }
}